import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomTable from "../CustomTable";
import CustomButton from "../CustomButton";
import moment from "moment";
import DatePicker from "../FormFields/DatePicker";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { storeColumns } from "../Columns/Vouchers/AllVouchers";
import { l1Columns } from "../Columns/Vouchers/PendingVouchers";
import { useAuth } from "../../contexts/AuthContext";
import { downloadReports, downloadReportsFun, getArrayOfSelectedStoresCode } from "../../utils/helpers";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { toast } from "react-toastify";
import {
  normalizeVoucherParentRowForStoreColumns,
  resolveVoucherListRowId,
  storeVoucherParentsFromGetRecordsResponse,
} from "../../utils/dmsStandaloneListUtils";

const ReworkHistory = () => {
  const { data = [], apiCall } = useApi();
  const {data: fileData,  apiCall: fileDownload } = useApi();
  const {data: fileStoreData,  apiCall: fileDownloadAtStore } = useApi();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loc = useLocation()
  const [startDate, setStartDate] = useState(
    moment(
      searchParams.get("startDate") || moment().subtract({ days: 7 }).toDate()
    ).toDate()
  );
  const [endDate, setEndDate] = useState(
    moment(searchParams.get("endDate") || new Date()).toDate()
  );
  const {
    user: { userLabel = "" },
    selectedStores,
    storeDropdownState,
    currentStoreSelect, 
    setCurrentStoreSelect
  } = useAuth();

  const { name: storeName } =
    selectedStores?.storeDetails || selectedStores?.[0] || {name:""};
  const selectedStoreCode =
    selectedStores?.storeDetails?.storeCode ||
    selectedStores?.storeCode ||
    selectedStores?.[0]?.storeCode ||
    "";

  const [selectedCheckBoxes, setSelectedCheckBoxes] = useState([]);
  const [tableData, setTableData] = useState();
  const inFlightRequestKeyRef = useRef(null);
  const lastCompletedRequestKeyRef = useRef(null);

  let pathVar = loc?.pathname?.split("/") || ""
  let subVoucherPath = pathVar[pathVar.length - 1]

  const mapSubVoucherData = () =>{
    let currentSubVoucherData = [];
    const storeData = data?.storeGetResponse || data?.storeWiseRecordsResponses || [];
    const getStoreCode = (row) =>
      row?.storeCode || row?.store?.storeCode || row?.store?.code || "";
    const getStoreName = (row) =>
      row?.storeName || row?.store?.name || row?.store?.storeName || "";
    const getStoreChildren = (row) => {
      const docs = Array.isArray(row?.documents) ? row.documents : [];
      if (docs.length > 0) return docs;
      return Array.isArray(row?.dataModalRecordsResponse)
        ? row.dataModalRecordsResponse
        : [];
    };
    const findStore = (codeHint, nameHint) => {
      const normalizedCode = String(codeHint || "").trim().toLowerCase();
      const normalizedName = String(nameHint || "").trim().toLowerCase();
      if (normalizedCode) {
        const byCode = storeData.find(
          (i) => getStoreCode(i)?.toLowerCase() === normalizedCode
        );
        if (byCode) return byCode;
      }
      if (normalizedName) {
        const byName = storeData.find(
          (i) => getStoreName(i)?.toLowerCase() === normalizedName
        );
        if (byName) return byName;
      }
      return storeData?.find((i) => getStoreChildren(i).length > 0) || null;
    };
      if(currentStoreSelect){
        const selectedStore = findStore("", currentStoreSelect);
        currentSubVoucherData = getStoreChildren(selectedStore);
      }else if(storeName && storeName !== 'All'){
        const selectedStore = findStore(selectedStoreCode, storeName);
        currentSubVoucherData = getStoreChildren(selectedStore);
      }else{
        currentSubVoucherData = storeData?.length > 0 
          ? getStoreChildren(storeData[0])
          : [];
      }
      return currentSubVoucherData.map(normalizeVoucherParentRowForStoreColumns);
  }

  useEffect(() => {
    if (subVoucherPath === "sub-voucher") {
      setTableData(mapSubVoucherData() || []);
    }
  }, [data]);

  useEffect(() => {
    let nextData = [];
    if (subVoucherPath === "vouchers") {
      // For non-Store users, data structure is: { storeGetResponse: [...] }
      // For Store users, data is already a flat array
      if (userLabel === "Store") {
        nextData = storeVoucherParentsFromGetRecordsResponse(data);
      } else {
        nextData = data?.storeGetResponse || data?.storeWiseRecordsResponses || [];
      }
    } else {
      nextData = mapSubVoucherData();
    }
    setTableData(nextData);
  }, [subVoucherPath, data, userLabel]);

  const voucherApiCall = async (start, end) => {
    const startTs = moment(start).startOf("d").format("YYYY-MM-DD HH:mm:ss");
    const endTs = moment(end || start).endOf("d").format("YYYY-MM-DD HH:mm:ss");
    const storeCodes = getArrayOfSelectedStoresCode(selectedStores, storeDropdownState);
    const normalizedStoreCodes = Array.isArray(storeCodes) ? [...storeCodes].sort() : [];
    const requestKey = `${startTs}|${endTs}|${normalizedStoreCodes.join(",")}`;
    if (inFlightRequestKeyRef.current === requestKey || lastCompletedRequestKeyRef.current === requestKey) {
      return;
    }
    inFlightRequestKeyRef.current = requestKey;
    navigate(
      {
        pathname: window.location.pathname,
        search: createSearchParams({
          startDate: start,
          endDate: end,
        }).toString(),
      },
      { replace: true }
    );
    try {
      await apiCall({
      url: apiUrls.dmsService.getRecords,
      dataToSend: {
        startDate: startTs,
        metadataSchemaId: 4,
        endDate: endTs,
        searchText: "",
        status: "REWORK",
        storeCodes: normalizedStoreCodes,
      },
      method: "post",
      // REWORK (and current prod API) returns storeWiseRecordsResponses, not storeGetResponse;
      // boolean processData + storeGetResponse leaves data undefined for Store.
    });
      lastCompletedRequestKeyRef.current = requestKey;
    } finally {
      if (inFlightRequestKeyRef.current === requestKey) {
        inFlightRequestKeyRef.current = null;
      }
    }
  };

  const isStoreActive = userLabel === "Store";
  const storeFetchDependencyKey = useMemo(() => {
    const selectedCode =
      selectedStores?.storeDetails?.storeCode ||
      selectedStores?.storeCode ||
      selectedStores?.value ||
      "";
    const dropdownCount = Array.isArray(storeDropdownState)
      ? storeDropdownState.length
      : storeDropdownState
      ? 1
      : 0;
    return `${selectedCode}|${dropdownCount}`;
  }, [
    selectedStores?.storeDetails?.storeCode,
    selectedStores?.storeCode,
    selectedStores?.value,
    Array.isArray(storeDropdownState) ? storeDropdownState.length : 0,
  ]);

  useEffect(() => {
    voucherApiCall(startDate, endDate);
  }, [storeFetchDependencyKey]);

  useEffect(() => {
    if (data && !isStoreActive && subVoucherPath !== "sub-voucher") {
      setTableData(data?.storeGetResponse || data?.storeWiseRecordsResponses || []);
    }
  }, [data, isStoreActive, subVoucherPath]);

  const onChangeDate = (dates) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      if (!start) return;
      setStartDate(start);
      setEndDate(end || null);
      if (end) voucherApiCall(start, end);
      return;
    }

    if (!dates) return;
    setStartDate(dates);
    setEndDate(dates);
    voucherApiCall(dates, dates);
  };

  const onClickChangeData = (data) => {
    setTableData(data);
  };

  const nonStoreColumns = useMemo(
    () => {
      const columns = l1Columns({
        navigate,
        editApprovalHandler: () => {},
        userActiveLevel: userLabel,
        approveRequestHandler: () => {},
        rejectRequestHandler: () => {},
        reworkRequestHandler: () => {},
        extraColumns: (subVoucherPath == "sub-voucher")
          ? [
              {
                Header: "V.ID",
                accessor: "id",
                Cell: (props) => {
                  const id = resolveVoucherListRowId(props?.row?.original);
                  const isPendingDocAvail = true;
                  
                  // console.log("🟡 ReworkHistory V.ID Cell rendered - id:", id, "isPendingDocAvail:", isPendingDocAvail, "rowData:", props.row?.original);
                  
                  if (!id) {
                    return <span>-</span>;
                  }
                  
                  const handleMouseEnter = (e) => {
                    // console.log("🟢 ReworkHistory V.ID MOUSE ENTER - id:", id, "target:", e.target);
                    e.target.style.backgroundColor = "#e3f2fd";
                  };
                  
                  const handleMouseLeave = (e) => {
                    // console.log("🔴 ReworkHistory V.ID MOUSE LEAVE - id:", id);
                    e.target.style.backgroundColor = "transparent";
                  };
                  
                  const handleClick = (e) => {
                    // console.log("🖱️ ReworkHistory V.ID CLICKED (onClick)! - id:", id, "event:", e);
                    // console.log("🖱️ Click target:", e.target);
                    // console.log("🖱️ Current target:", e.currentTarget);
                    
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                      e.nativeEvent.stopImmediatePropagation();
                    }
                    
                    // console.log("🖱️ After stopPropagation - navigating to /vouchers/" + id);
                    
                    if (isPendingDocAvail && id) {
                      try {
                        navigate(`/vouchers/${id}`, {
                          state: { ...props.row?.original, type: "Rework" },
                        });
                        // console.log("✅ ReworkHistory Navigation called successfully");
                      } catch (error) {
                        // console.error("❌ ReworkHistory Navigation error:", error);
                      }
                    } else {
                      // console.error("❌ Cannot navigate - isPendingDocAvail:", isPendingDocAvail, "id:", id);
                    }
                  };
                  
                  const handleMouseDown = (e) => {
                    // console.log("🖱️ ReworkHistory V.ID MOUSE DOWN - id:", id, "button:", e.button);
                    // Use mouseDown as primary handler since onClick might be blocked
                    if (e.button === 0) { // Left mouse button
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                        e.nativeEvent.stopImmediatePropagation();
                      }
                      
                      // console.log("🖱️ MouseDown - navigating to /vouchers/" + id);
                      
                      if (isPendingDocAvail && id) {
                        try {
                          navigate(`/vouchers/${id}`, {
                            state: { ...props.row?.original, type: "Rework" },
                          });
                          // console.log("✅ ReworkHistory Navigation called from MouseDown");
                        } catch (error) {
                          // console.error("❌ ReworkHistory Navigation error from MouseDown:", error);
                        }
                      }
                    }
                  };
                  
                  return (
                    <div
                      className={`${
                        isPendingDocAvail
                          ? "underline underline-offset-4 cursor-pointer"
                          : ""
                      }`}
                      style={{ 
                        color: isPendingDocAvail ? "blue" : "black",
                        cursor: isPendingDocAvail ? "pointer" : "default",
                        pointerEvents: "auto",
                        zIndex: 9999,
                        position: "relative",
                        userSelect: "none",
                        WebkitUserSelect: "none"
                      }}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      onClick={handleClick}
                      onMouseDown={handleMouseDown}
                      onMouseUp={(e) => {
                        // console.log("🖱️ ReworkHistory V.ID MOUSE UP - id:", id, "button:", e.button);
                      }}
                      onContextMenu={(e) => {
                        // console.log("🖱️ ReworkHistory V.ID RIGHT CLICK - id:", id);
                        e.preventDefault(); // Prevent right-click menu
                      }}
                    >
                      {id}
                    </div>
                  );
                },
              },
            ]
          : [
              {
                Header: "Store Name",
                accessor: "storeName",
                Cell: (props) => {
                  const storeName = props?.row?.original?.storeName;
                  return (
                    <div
                      className="underline underline-offset-4 cursor-pointer"
                      style={{ color: "blue" }}
                      onClick={() => {
                        setCurrentStoreSelect(storeName);
                        navigate("/vouchers/sub-voucher", {
                          state: { type: "Rework" },
                        });
                      }}
                    >
                      {storeName}
                    </div>
                  );
                },
              },
            ],
        inBetweenColumns: [],
      });
      // Remove Actions column from Rework History
      return columns.filter(col => col.accessor !== "actions");
    },
    [userLabel, navigate, subVoucherPath, setCurrentStoreSelect]
  );

  const storeColumnsData = useMemo(
    () =>
      storeColumns({
        onClickChangeData,
        navigate,
        downloadVoucher: (rowData, status) => {
          const st = `${moment(startDate).format("YYYY-MM-DD")} 00:00:00`;
          const et = `${moment(endDate).format("YYYY-MM-DD")} 23:59:59`;
          fileDownloadAtStore({
            url: `${apiUrls.dmsService.downloadVoucherStoreLevel}`,
            dataToSend: {
              storeCodes: [rowData?.storeCode],
              status: status,
              metadataSchemaId: 4,
              endDate: et,
              startDate: st,
            },
            method: "post",
            responseType: "blob",
            responseData: (res) => {
              const { data = "", status = null } = res || {};
              const contentType = res?.headers?.["content-type"];
              const contentDisposition = res?.headers["content-disposition"];
              const fileNameMatch =
                contentDisposition &&
                contentDisposition.match(/filename="(.+)"/);
              const fileName = fileNameMatch
                ? fileNameMatch[1]
                : `voucher_${status}_${moment().format("YYYY-MM-DD")}.xlsx`;

              if (status === 200) {
                downloadReportsFun(data, fileName, contentType);
              }
            },
          });
        },
      }),
    [onClickChangeData, navigate, startDate, endDate, fileDownloadAtStore]
  );

  const downloadVoucher = (rowData, status) => {
    const st = `${moment(startDate).format("YYYY-MM-DD")} 00:00:00`;
    const et = `${moment(endDate).format("YYYY-MM-DD")} 23:59:59`;
    fileDownloadAtStore({
      url: `${apiUrls.dmsService.downloadVoucherStoreLevel}`,
      dataToSend: {
        storeCodes: getArrayOfSelectedStoresCode(selectedStores, storeDropdownState),
        status: status,
        metadataSchemaId: 4,
        endDate: et,
        startDate: st,
      },
      method: "post",
      responseType: "blob",
      responseData: (res) => {
        const { data = "", status = null } = res || {};
        const contentType = res?.headers?.["content-type"];
        const contentDisposition = res?.headers["content-disposition"];
        const fileNameMatch =
          contentDisposition &&
          contentDisposition.match(/filename="(.+)"/);
        const fileName = fileNameMatch
          ? fileNameMatch[1]
          : `voucher_${status}_${moment().format("YYYY-MM-DD")}.xlsx`;

        if (status === 200) {
          downloadReportsFun(data, fileName, contentType);
        }
      },
    });
  };

  return (
    <div className="flex flex-col py-2">
      <div className="flex flex-col mb-4 gap-y-3 mt-3 relative z-[99999]">
        <DatePicker
          dateRange
          startDate={startDate}
          endDate={endDate}
          onChange={onChangeDate}
          maxDate={new Date()}
          withPortal
          wrapperClassName="self-end relative z-[99999] pointer-events-auto"
          datePickerClassName="relative z-[99999] pointer-events-auto"
        />
      </div>
      <CustomTable
        columns={isStoreActive ? storeColumnsData : nonStoreColumns}
        data={
          tableData
            ? tableData
            : userLabel === "Store"
            ? storeVoucherParentsFromGetRecordsResponse(data)
            : data?.storeGetResponse || data?.storeWiseRecordsResponses || []
        }
        onCheckboxSelect={(checkboxes) => {
          const data1 = tableData
            ? tableData
            : userLabel === "Store"
            ? storeVoucherParentsFromGetRecordsResponse(data)
            : data?.storeGetResponse || data?.storeWiseRecordsResponses || [];
          if (data1?.length) {
            setSelectedCheckBoxes(checkboxes);
          }
        }}
        showCheckboxes={false}
      />
    </div>
  );
};

export default ReworkHistory;
